import React, { useState, useRef, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  TextField,
  Button,
  IconButton,
  Divider,
  Switch,
  FormControlLabel,
  Tooltip,
  CircularProgress,
  makeStyles
} from "@material-ui/core";
import {
  Close,
  PlayArrow,
  Stop,
  VolumeUp,
  Speed,
  RecordVoiceOver,
  Settings,
  Restore
} from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import toastUtils from "../../utils/toastUtils";

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden"
  },
  header: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: theme.palette.background.paper
  },
  content: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles
  },
  section: {
    marginBottom: theme.spacing(3)
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    color: theme.palette.primary.main
  },
  voiceOption: {
    padding: theme.spacing(1.5),
    border: `2px solid transparent`,
    borderRadius: theme.shape.borderRadius,
    cursor: "pointer",
    transition: "all 0.3s ease",
    marginBottom: theme.spacing(1),
    "&:hover": {
      backgroundColor: theme.palette.action.hover
    }
  },
  selectedVoice: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.light + "10"
  },
  voiceHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(0.5)
  },
  voiceInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  voiceDescription: {
    fontSize: 12,
    color: theme.palette.text.secondary
  },
  playButton: {
    width: 32,
    height: 32,
    minWidth: 32
  },
  sliderContainer: {
    marginBottom: theme.spacing(2)
  },
  sliderLabel: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  previewSection: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`
  },
  previewControls: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2)
  },
  actions: {
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    gap: theme.spacing(1),
    justifyContent: "flex-end"
  }
}));

const VOICE_OPTIONS = [
  {
    id: "alloy",
    name: "Alloy",
    description: "Voz neutra e equilibrada, ideal para uso geral",
    gender: "neutro"
  },
  {
    id: "echo",
    name: "Echo", 
    description: "Voz masculina clara e profissional",
    gender: "masculino"
  },
  {
    id: "fable",
    name: "Fable",
    description: "Voz feminina suave e calorosa",
    gender: "feminino"
  },
  {
    id: "onyx",
    name: "Onyx",
    description: "Voz masculina profunda e autoritativa",
    gender: "masculino"
  },
  {
    id: "nova",
    name: "Nova",
    description: "Voz feminina jovem e energética",
    gender: "feminino"
  },
  {
    id: "shimmer",
    name: "Shimmer",
    description: "Voz feminina elegante e sofisticada",
    gender: "feminino"
  }
];

const VoiceSettingsPanel = ({
  settings,
  onSettingsChange,
  onClose,
  embedded = false
}) => {
  const classes = useStyles();
  
  // State
  const [localSettings, setLocalSettings] = useState({
    selectedVoice: "alloy",
    speed: 1.0,
    volume: 0.8,
    autoPlay: true,
    testText: i18n.t("voiceAssistant.ttsSettings.defaultTestText"),
    ...settings
  });
  const [isPlaying, setIsPlaying] = useState(false);
  const [playingVoice, setPlayingVoice] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Refs
  const audioRef = useRef(null);

  // Handle local settings change
  const handleSettingChange = useCallback((key, value) => {
    setLocalSettings(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  // Apply settings
  const applySettings = useCallback(() => {
    onSettingsChange(localSettings);
    toastUtils.success("Configurações aplicadas com sucesso!", "Configurações Aplicadas");
  }, [localSettings, onSettingsChange]);

  // Reset settings
  const resetSettings = useCallback(() => {
    const defaultSettings = {
      selectedVoice: "alloy",
      speed: 1.0,
      volume: 0.8,
      autoPlay: true,
      testText: i18n.t("voiceAssistant.ttsSettings.defaultTestText")
    };
    setLocalSettings(defaultSettings);
    onSettingsChange(defaultSettings);
    toastUtils.info("Configurações redefinidas para o padrão", "Configurações Redefinidas");
  }, [onSettingsChange]);

  // Play voice preview
  const playVoicePreview = useCallback(async (voiceId) => {
    try {
      setIsGenerating(true);
      setPlayingVoice(voiceId);

      // Stop current audio if playing
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
        setIsPlaying(false);
      }

      // Generate TTS preview (simulate API call)
      const testText = localSettings.testText || i18n.t("voiceAssistant.ttsSettings.defaultTestText");
      
      // Simulate TTS generation
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // In a real implementation, this would be an API call to generate TTS
      // For now, we'll just simulate it
      const audioUrl = `data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmEcCzuL1/LFdi4G...`;
      
      audioRef.current = new Audio(audioUrl);
      audioRef.current.volume = localSettings.volume;
      audioRef.current.playbackRate = localSettings.speed;
      
      audioRef.current.onended = () => {
        setIsPlaying(false);
        setPlayingVoice(null);
      };
      
      audioRef.current.onerror = () => {
        setIsPlaying(false);
        setPlayingVoice(null);
        toastError({ message: "Erro ao reproduzir preview da voz" });
      };
      
      await audioRef.current.play();
      setIsPlaying(true);
      
    } catch (error) {
      console.error("Error playing voice preview:", error);
      toastError(error);
      setIsPlaying(false);
      setPlayingVoice(null);
    } finally {
      setIsGenerating(false);
    }
  }, [localSettings.testText, localSettings.volume, localSettings.speed]);

  // Stop preview
  const stopPreview = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsPlaying(false);
    setPlayingVoice(null);
  }, []);

  return (
    <Box className={classes.root}>
      {/* Header */}
      {!embedded && (
      <Box className={classes.header}>
        <Box display="flex" alignItems="center" gap={1}>
          <Settings />
          <Typography variant="h6">
            {i18n.t("voiceAssistant.ttsSettings.title")}
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </Box>
      )}

      {/* Content */}
      <Box className={classes.content}>
        {/* Voice Selection */}
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle} variant="subtitle1">
            {i18n.t("voiceAssistant.ttsSettings.selectVoice")}
          </Typography>
          
          {VOICE_OPTIONS.map((voice) => (
            <Paper
              key={voice.id}
              className={`${classes.voiceOption} ${
                localSettings.selectedVoice === voice.id ? classes.selectedVoice : ""
              }`}
              onClick={() => handleSettingChange("selectedVoice", voice.id)}
              elevation={localSettings.selectedVoice === voice.id ? 2 : 0}
            >
              <Box className={classes.voiceHeader}>
                <Box className={classes.voiceInfo}>
                  <RecordVoiceOver fontSize="small" />
                  <Box>
                    <Typography variant="subtitle2">
                      {voice.name}
                    </Typography>
                    <Typography variant="caption" className={classes.voiceDescription}>
                      {voice.description}
                    </Typography>
                  </Box>
                </Box>
                
                <IconButton
                  className={classes.playButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isPlaying && playingVoice === voice.id) {
                      stopPreview();
                    } else {
                      playVoicePreview(voice.id);
                    }
                  }}
                  disabled={isGenerating}
                >
                  {isGenerating && playingVoice === voice.id ? (
                    <CircularProgress size={16} />
                  ) : isPlaying && playingVoice === voice.id ? (
                    <Stop fontSize="small" />
                  ) : (
                    <PlayArrow fontSize="small" />
                  )}
                </IconButton>
              </Box>
            </Paper>
          ))}
        </Box>

        <Divider />

        {/* Speed Settings */}
        <Box className={classes.section}>
          <Box className={classes.sliderContainer}>
            <Box className={classes.sliderLabel}>
              <Speed fontSize="small" />
              <Typography variant="subtitle2">
                {i18n.t("voiceAssistant.ttsSettings.speed")}
              </Typography>
              <Typography variant="caption">
                ({localSettings.speed}x)
              </Typography>
            </Box>
            <Slider
              value={localSettings.speed}
              onChange={(event, value) => handleSettingChange("speed", value)}
              min={0.25}
              max={4.0}
              step={0.25}
              marks={[
                { value: 0.5, label: "0.5x" },
                { value: 1.0, label: "1x" },
                { value: 1.5, label: "1.5x" },
                { value: 2.0, label: "2x" }
              ]}
              valueLabelDisplay="auto"
            />
          </Box>
        </Box>

        {/* Volume Settings */}
        <Box className={classes.section}>
          <Box className={classes.sliderContainer}>
            <Box className={classes.sliderLabel}>
              <VolumeUp fontSize="small" />
              <Typography variant="subtitle2">
                {i18n.t("voiceAssistant.ttsSettings.volume")}
              </Typography>
              <Typography variant="caption">
                ({Math.round(localSettings.volume * 100)}%)
              </Typography>
            </Box>
            <Slider
              value={localSettings.volume}
              onChange={(event, value) => handleSettingChange("volume", value)}
              min={0}
              max={1}
              step={0.05}
              marks={[
                { value: 0, label: "0%" },
                { value: 0.5, label: "50%" },
                { value: 1, label: "100%" }
              ]}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
            />
          </Box>
        </Box>

        <Divider />

        {/* Auto-play Setting */}
        <Box className={classes.section}>
          <FormControlLabel
            control={
              <Switch
                checked={localSettings.autoPlay}
                onChange={(event) => handleSettingChange("autoPlay", event.target.checked)}
                color="primary"
              />
            }
            label="Reproduzir automaticamente respostas de áudio"
          />
        </Box>

        <Divider />

        {/* Test Text */}
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle} variant="subtitle1">
            {i18n.t("voiceAssistant.ttsSettings.testText")}
          </Typography>
          
          <Box className={classes.previewSection}>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={localSettings.testText}
              onChange={(event) => handleSettingChange("testText", event.target.value)}
              placeholder={i18n.t("voiceAssistant.ttsSettings.defaultTestText")}
              variant="outlined"
              size="small"
            />
            
            <Box className={classes.previewControls}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={isPlaying ? <Stop /> : <PlayArrow />}
                onClick={() => {
                  if (isPlaying) {
                    stopPreview();
                  } else {
                    playVoicePreview(localSettings.selectedVoice);
                  }
                }}
                disabled={isGenerating || !localSettings.testText.trim()}
              >
                {isPlaying ? "Parar" : i18n.t("voiceAssistant.ttsSettings.playPreview")}
              </Button>
              
              <Button
                size="small"
                onClick={() => handleSettingChange("testText", i18n.t("voiceAssistant.ttsSettings.defaultTestText"))}
              >
                Resetar Texto
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Actions */}
      {!embedded && (
      <Box className={classes.actions}>
        <Button
          variant="outlined"
          startIcon={<Restore />}
          onClick={resetSettings}
        >
          {i18n.t("voiceAssistant.ttsSettings.resetSettings")}
        </Button>
        
        <Button
          variant="contained"
          color="primary"
          onClick={applySettings}
        >
          {i18n.t("voiceAssistant.ttsSettings.applySettings")}
        </Button>
      </Box>
      )}
    </Box>
  );
};

export default VoiceSettingsPanel;